import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";

// Project modules
import "../../App.css";
import { GameContext } from "../../Context/GameContext";
import { roomLabels } from "../../Config/config";
import { RoomStatus, type ProfileSettings } from "../../Types/Game";
import ControlButtons from "../../Components/ControlButtons/ControlButtons";

/**
 * Trapped
 * -------
 * End-state screen shown when the timer expires in a room.
 * Behaviour:
 * - Marks the current room status as `Trapped`.
 * - Resets the start time for potential subsequent attempts.
 * - Button returns to the start; in single-room mode, resets the whole game.
 */

// Narrow the room-id type to the known keys in roomLabels
type RoomId = keyof typeof roomLabels;

const Trapped: React.FC = () => {
  /** CONTEXT ------------------------------------------------------- */
  const {
    teamName,
    currentRoom,
    resetRoomProgress,
    setProfileSettings,
    setStartTime,
    profileSettings,
    resetGame,
  } = useContext(GameContext);

  /** EFFECTS ------------------------------------------------------- */
  // On mount: mark current room as trapped and reset timer baseline
  useEffect(() => {
    if (!currentRoom) return;

    setProfileSettings((prev: ProfileSettings | undefined) =>
      prev
        ? {
            ...prev,
            rooms: {
              ...prev.rooms,
              [currentRoom as RoomId]: {
                ...prev.rooms[currentRoom as RoomId],
                status: RoomStatus.Trapped,
              },
            },
          }
        : prev
    );

    // Reset the timer reference (in seconds)
    setStartTime(Math.floor(Date.now() / 1000));
  }, [currentRoom, setProfileSettings, setStartTime]);

  /** DERIVED ------------------------------------------------------- */
  const trappedRoom: RoomId | "unknown" = (currentRoom as RoomId) ?? "unknown";
  const trappedLabel =
    trappedRoom === "unknown" ? "this room" : roomLabels[trappedRoom];

const trappedLabelWithRoom = /\broom\b/i.test(trappedLabel)
  ? trappedLabel
  : `${trappedLabel} Room`;

  const isSingleRoom = Boolean(profileSettings?.singleRoom);

  /** RENDER -------------------------------------------------------- */
  return (
    <div className="escape-room" style={{ overflow: "hidden" }}>
      <div id="escapeRoomContainer">
        <ControlButtons />

        <div className="trapped-room" id={String(trappedRoom)}>
          <div className="slime">
            <div className="trapped-fade-in" style={{ width: "100%" }}>
              <div className="trapped-title" />
              <div className="trapped-content">
                <h1 style={{ marginTop: 16 }}>Time&apos;s up {teamName}!</h1>

                <p>
                  The <strong>{trappedLabelWithRoom}</strong> fills with slime and you remain stuck here forever!
                </p>

                <Link to="/">
                  <button
                    className="btn-blue"
                    style={{ marginTop: 8, marginBottom: 8 }}
                    onClick={isSingleRoom ? resetGame : resetRoomProgress}
                  >
                    End the challenge
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trapped;